import React, { useEffect, useState, useCallback, useRef } from "react";
import { wasteApi } from "@/api/waste";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Save, ChevronDown, Loader2 } from "lucide-react";

function makeClientId() {
  return `cli_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

const SHIFTS = [
  { value: "esmorzar", label: "Esmorzar" },
  { value: "dinar",    label: "Dinar" },
  { value: "sopar",    label: "Sopar" },
  { value: "altre",    label: "Altre" },
];

export default function WasteQuickForm({ venueId, settings, onSaved }) {
  const [productName, setProductName] = useState("");
  const [ingredientId, setIngredientId] = useState(null);
  const [family, setFamily] = useState("altres");
  const [reason, setReason] = useState("caducitat");
  const [zone, setZone] = useState("cuina");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("kg");
  const [unitCost, setUnitCost] = useState("");
  const [notes, setNotes] = useState("");
  const [shift, setShift] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [saving, setSaving] = useState(false);
  const debRef = useRef(null);

  const total = (parseFloat(unitCost || 0) || 0) * (parseFloat(quantity || 0) || 0);

  const fetchSuggestions = useCallback(async (q) => {
    if (!venueId) return;
    try {
      const items = await wasteApi.productSuggestions(venueId, q);
      setSuggestions(items || []);
    } catch (e) { /* noop */ }
  }, [venueId]);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => fetchSuggestions(productName), 250);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [productName, fetchSuggestions]);

  const pickSuggestion = (s) => {
    setProductName(s.name);
    setShowSuggest(false);
    if (s.source === "ingredient") {
      setIngredientId(s.ingredient_id);
      if (s.unit) setUnit(s.unit);
      if (s.cost_per_unit != null) setUnitCost(String(s.cost_per_unit));
      // Map ingredient category → family if it matches a known one
      const cat = (s.category || "").toLowerCase();
      const fam = (settings?.product_families || []).find((f) => f.code === cat || f.label.toLowerCase().includes(cat));
      if (fam) setFamily(fam.code);
    } else {
      setIngredientId(null);
    }
  };

  const reset = () => {
    setProductName(""); setIngredientId(null);
    setQuantity(""); setUnitCost(""); setNotes("");
    setReason("caducitat"); setZone("cuina"); setShift("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!productName.trim()) { toast.error("Indica el producte"); return; }
    if (!quantity || parseFloat(quantity) <= 0) { toast.error("Quantitat invàlida"); return; }
    setSaving(true);
    try {
      const payload = {
        venue_id: venueId,
        client_id: makeClientId(),
        product_name: productName.trim(),
        ingredient_id: ingredientId,
        family, reason, zone,
        quantity: parseFloat(quantity),
        unit,
        unit_cost: unitCost ? parseFloat(unitCost) : null,
        notes: notes || null,
        shift: shift || null,
      };
      await wasteApi.createRecord(payload);
      toast.success("Merma registrada");
      reset();
      if (onSaved) onSaved();
    } catch (err) {
      // Offline fallback: queue via service worker
      if (!navigator.onLine) {
        toast.info("Sense connexió — guardat per sincronitzar més tard");
        reset();
        if (onSaved) onSaved();
      } else {
        toast.error(err.message || "Error guardant");
      }
    } finally {
      setSaving(false);
    }
  };

  const families = settings?.product_families || [];
  const reasons = settings?.waste_reasons || [];
  const zones = settings?.zones || [];
  const units = settings?.units || ["kg", "g", "l", "ml", "unitats"];

  return (
    <Card className="p-4 sm:p-6" data-testid="waste-quick-form">
      <form onSubmit={submit} className="space-y-4">
        {/* Producte amb autocompletat */}
        <div className="relative">
          <Label className="text-xs font-semibold text-gray-700">Producte *</Label>
          <Input
            data-testid="waste-product-input"
            value={productName}
            onChange={(e) => { setProductName(e.target.value); setShowSuggest(true); setIngredientId(null); }}
            onFocus={() => setShowSuggest(true)}
            onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
            placeholder="Ex: Tomàquet, Pa, Pollastre rostit…"
            className="mt-1 text-base"
            autoComplete="off"
          />
          {showSuggest && suggestions.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 bg-white rounded-xl border shadow-lg max-h-60 overflow-y-auto">
              {suggestions.map((s, i) => (
                <button
                  type="button"
                  key={`${s.source}-${s.name}-${i}`}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between text-sm"
                  onClick={() => pickSuggestion(s)}
                  data-testid={`waste-suggestion-${i}`}
                >
                  <span>{s.name}</span>
                  <span className="text-xs text-gray-400">
                    {s.source === "ingredient" ? `${s.unit || ""} · ${s.cost_per_unit != null ? s.cost_per_unit + " €" : ""}` : "Historial"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quantitat + Unitat */}
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Label className="text-xs font-semibold text-gray-700">Quantitat *</Label>
            <Input
              data-testid="waste-quantity-input"
              type="number" min="0" step="0.01" inputMode="decimal"
              value={quantity} onChange={(e) => setQuantity(e.target.value)}
              placeholder="0.00"
              className="mt-1 text-base"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold text-gray-700">Unitat</Label>
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className="mt-1" data-testid="waste-unit-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {units.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Motiu + Zona */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs font-semibold text-gray-700">Motiu *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1" data-testid="waste-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {reasons.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-gray-700">Zona</Label>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger className="mt-1" data-testid="waste-zone-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {zones.map((z) => <SelectItem key={z.code} value={z.code}>{z.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Família + Torn */}
        <details className="rounded-xl bg-gray-50 px-3 py-2">
          <summary className="text-sm font-semibold text-gray-700 cursor-pointer flex items-center justify-between">
            Detalls avançats
            <ChevronDown className="w-4 h-4" />
          </summary>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-semibold text-gray-700">Família</Label>
              <Select value={family} onValueChange={setFamily}>
                <SelectTrigger className="mt-1" data-testid="waste-family-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {families.map((f) => <SelectItem key={f.code} value={f.code}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">Torn</Label>
              <Select value={shift} onValueChange={setShift}>
                <SelectTrigger className="mt-1" data-testid="waste-shift-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {SHIFTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">Cost unitari (€)</Label>
              <Input
                data-testid="waste-unitcost-input"
                type="number" min="0" step="0.01" inputMode="decimal"
                value={unitCost} onChange={(e) => setUnitCost(e.target.value)}
                placeholder="—" className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">Cost total estimat</Label>
              <div className="mt-1 px-3 py-2 rounded-lg bg-white border text-sm font-semibold text-[#7B2D3F]">
                {total.toFixed(2)} €
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold text-gray-700">Notes</Label>
              <Textarea
                data-testid="waste-notes-input"
                value={notes} onChange={(e) => setNotes(e.target.value)}
                rows={2} className="mt-1"
                placeholder="Detalls addicionals (opcional)"
              />
            </div>
          </div>
        </details>

        <div className="flex items-center gap-2 pt-2">
          <Button type="button" variant="outline" onClick={reset} data-testid="waste-form-reset">
            <Trash2 className="w-4 h-4 mr-2" /> Buidar
          </Button>
          <Button type="submit" className="flex-1 bg-[#7B2D3F] hover:bg-[#5B1D2F]" disabled={saving} data-testid="waste-form-submit">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Registrar merma
          </Button>
        </div>
      </form>
    </Card>
  );
}
